import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft, PaintBucket, Brush, Eraser, Sticker as StickerIcon,
  Undo2, Redo2, Trash2, Download, Save, PartyPopper, Volume2, VolumeX, Lock,
} from "lucide-react";
import { toast } from "sonner";
import ImageStudio from "@/components/ImageStudio";
import PageSVG, { pointsToPath } from "@/components/PageSVG";
import { getPage, getCategory } from "@/data/registry";
import { STICKERS, STICKERS_BY_ID, STICKER_IDS } from "@/data/stickers";
import { fetchProgress, completePage, saveArtwork } from "@/lib/api";
import { sfx, isMuted, setMuted } from "@/lib/sounds";

const PALETTE = [
  "#FF7A00", "#FFA24D", "#FFD23F", "#39FF14",
  "#7FF0D0", "#2EC5FF", "#2B4EFF", "#8A4FFF",
  "#4B2A85", "#FF5CA8", "#E63329", "#8B4A2F",
  "#B9B4C7", "#16121F", "#9EF01A", "#FFFDF8",
];

const initial = { past: [], fills: {}, strokes: [], placed: [], future: [] };

function push(state, next) {
  return {
    ...next,
    past: [...state.past, { fills: state.fills, strokes: state.strokes, placed: state.placed }].slice(-40),
    future: [],
  };
}

function reducer(state, action) {
  switch (action.type) {
    case "FILL":
      return push(state, { ...state, fills: { ...state.fills, [action.idx]: action.color } });
    case "ADD_STROKE":
      return push(state, { ...state, strokes: [...state.strokes, action.stroke] });
    case "ERASE": {
      const near = (p) => (p.x - action.p.x) ** 2 + (p.y - action.p.y) ** 2 < action.r * action.r;
      const strokes = state.strokes.filter((st) => !st.points.some(near));
      const placed = state.placed.filter((st) => !near({ x: st.x, y: st.y }));
      if (strokes.length === state.strokes.length && placed.length === state.placed.length) return state;
      return push(state, { ...state, strokes, placed });
    }
    case "ADD_STICKER":
      return push(state, { ...state, placed: [...state.placed, action.st] });
    case "UNDO": {
      if (!state.past.length) return state;
      const prev = state.past[state.past.length - 1];
      return { ...prev, past: state.past.slice(0, -1), future: [state, ...state.future].slice(0, 40) };
    }
    case "REDO": {
      if (!state.future.length) return state;
      const next = state.future[0];
      return { ...next, past: [...state.past, { fills: state.fills, strokes: state.strokes, placed: state.placed }], future: state.future.slice(1) };
    }
    case "RESET":
      return push(state, { ...state, fills: {}, strokes: [], placed: [] });
    default:
      return state;
  }
}

function Confetti() {
  const pieces = useMemo(
    () =>
      Array.from({ length: 46 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        delay: Math.random() * 0.8,
        dur: 1.6 + Math.random() * 1.4,
        color: PALETTE[i % 10],
        size: 6 + Math.random() * 8,
        rot: Math.random() * 360,
      })),
    []
  );
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {pieces.map((p) => (
        <motion.span
          key={p.id}
          className="absolute top-0 block rounded-sm"
          style={{ left: `${p.x}%`, width: p.size, height: p.size * 0.5, background: p.color }}
          initial={{ y: -30, opacity: 1, rotate: p.rot }}
          animate={{ y: "105vh", opacity: [1, 1, 0.4], rotate: p.rot + 540 }}
          transition={{ duration: p.dur, delay: p.delay, ease: "easeIn" }}
        />
      ))}
    </div>
  );
}

function SvgStudio() {
  const { pageId } = useParams();
  const navigate = useNavigate();
  const page = getPage(pageId);
  const cat = page ? getCategory(page.category) : null;

  const [state, dispatch] = useReducer(reducer, initial);
  const [tool, setTool] = useState("fill");
  const [color, setColor] = useState(PALETTE[0]);
  const [brushSize, setBrushSize] = useState(8);
  const [stickerId, setStickerId] = useState(STICKER_IDS[0]);
  const [stickerSize, setStickerSize] = useState(1);
  const [live, setLive] = useState(null);
  const [sparks, setSparks] = useState([]);
  const [muted, setM] = useState(isMuted());
  const [clearArmed, setClearArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [saved, setSaved] = useState(false);
  const [locked, setLocked] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const svgRef = useRef(null);
  const drawingRef = useRef(null);

  const fillable = page ? page.art.filter((p) => !p.line).length : 0;
  const colored = Object.keys(state.fills).length;
  const ready = fillable > 0 && colored >= Math.ceil(fillable * 0.6);

  useEffect(() => {
    if (!page) {
      navigate("/", { replace: true });
      return;
    }
    fetchProgress()
      .then((p) => {
        const c = getCategory(page.category);
        setLocked(c && p.count < c.unlockAt);
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [page, navigate]);

  if (!page) return null;

  const toSvg = (e) => {
    const rect = svgRef.current.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * 400,
      y: ((e.clientY - rect.top) / rect.height) * 500,
    };
  };

  const addSpark = (clientX, clientY, c) => {
    const rect = svgRef.current.getBoundingClientRect();
    const id = `${Date.now()}-${Math.random()}`;
    setSparks((s) => [...s, { id, x: clientX - rect.left, y: clientY - rect.top, c }]);
    setTimeout(() => setSparks((s) => s.filter((sp) => sp.id !== id)), 700);
  };

  const onPointerDown = (e) => {
    const p = toSvg(e);
    if (tool === "fill") {
      const i = e.target.getAttribute && e.target.getAttribute("data-i");
      if (i !== null && i !== undefined) {
        dispatch({ type: "FILL", idx: i, color });
        sfx.pop();
        addSpark(e.clientX, e.clientY, color);
      }
    } else if (tool === "brush") {
      drawingRef.current = { points: [p], color, size: brushSize };
      setLive({ ...drawingRef.current, id: "live" });
      sfx.scribble();
      svgRef.current.setPointerCapture(e.pointerId);
    } else if (tool === "eraser") {
      dispatch({ type: "ERASE", p, r: 16 });
      sfx.click();
    } else if (tool === "sticker") {
      dispatch({ type: "ADD_STICKER", st: { id: `${Date.now()}`, stickerId, x: p.x, y: p.y, s: stickerSize } });
      sfx.boop();
      addSpark(e.clientX, e.clientY, "#FFD23F");
    }
  };

  const onPointerMove = (e) => {
    if (!drawingRef.current) return;
    const p = toSvg(e);
    const pts = drawingRef.current.points;
    const last = pts[pts.length - 1];
    if ((p.x - last.x) ** 2 + (p.y - last.y) ** 2 > 4) {
      pts.push(p);
      setLive({ ...drawingRef.current, id: "live" });
    }
  };

  const onPointerUp = () => {
    if (drawingRef.current) {
      if (drawingRef.current.points.length > 1) {
        dispatch({ type: "ADD_STROKE", stroke: { ...drawingRef.current, id: `${Date.now()}` } });
      }
      drawingRef.current = null;
      setLive(null);
    }
  };

  const exportPng = () =>
    new Promise((resolve) => {
      const svg = svgRef.current.cloneNode(true);
      svg.setAttribute("width", "800");
      svg.setAttribute("height", "1000");
      svg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
      const data = new XMLSerializer().serializeToString(svg);
      const img = new Image();
      img.onload = () => {
        const c = document.createElement("canvas");
        c.width = 800;
        c.height = 1000;
        const ctx = c.getContext("2d");
        ctx.fillStyle = "#FFFDF8";
        ctx.fillRect(0, 0, 800, 1000);
        ctx.drawImage(img, 0, 0, 800, 1000);
        resolve(c.toDataURL("image/jpeg", 0.9));
      };
      img.onerror = () => resolve(null);
      img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(data);
    });

  const downloadPng = async () => {
    sfx.click();
    const url = await exportPng();
    if (!url) return toast.error("Could not download, try again");
    const a = document.createElement("a");
    a.href = url;
    a.download = `boo-scribbles-${page.id}.jpg`;
    a.click();
    toast.success("Saved to your device!");
  };

  const saveToGallery = async () => {
    sfx.click();
    const url = await exportPng();
    if (!url) return toast.error("Could not save, try again");
    try {
      await saveArtwork({ pageId: page.id, title: page.title, image: url });
      setSaved(true);
      sfx.sparkle();
      toast.success("Hung in the Haunted Gallery!");
    } catch (e) {
      toast.error("The gallery ghost dropped it — try again");
    }
  };

  const onFinish = async () => {
    if (busy) return;
    setBusy(true);
    sfx.fanfare();
    try {
      const res = await completePage(page.id);
      setResult(res);
      if (res.new_unlock) setTimeout(() => sfx.unlock(), 900);
    } catch (e) {
      toast.error("Magic hiccup — the reward didn't register");
    }
    setBusy(false);
  };

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    setM(next);
    if (!next) sfx.click();
  };

  const clearAll = () => {
    if (!clearArmed) {
      setClearArmed(true);
      sfx.click();
      setTimeout(() => setClearArmed(false), 2200);
      return;
    }
    dispatch({ type: "RESET" });
    sfx.pop();
    setClearArmed(false);
  };

  const toolBtn = (id, icon, label) => (
    <button
      key={id}
      data-testid={`tool-${id}-btn`}
      onClick={() => {
        setTool(id);
        sfx.click();
      }}
      aria-label={label}
      className={`flex h-12 w-12 items-center justify-center rounded-2xl border transition-all md:h-14 md:w-14 ${
        tool === id
          ? "border-pumpkin bg-pumpkin text-ink shadow-glow-orange"
          : "border-white/15 bg-white/5 text-paper/80 hover:border-pumpkin/60 hover:text-pumpkin"
      }`}
    >
      {icon}
    </button>
  );

  return (
    <div data-testid="studio-root" className="flex min-h-screen flex-col bg-ink text-paper">
      <header className="sticky top-0 z-40 flex items-center justify-between gap-3 border-b border-white/10 bg-ink/90 px-3 py-2.5 backdrop-blur-xl sm:px-6">
        <div className="flex min-w-0 items-center gap-3">
          <button
            data-testid="studio-back-btn"
            onClick={() => {
              sfx.click();
              navigate("/");
            }}
            aria-label="Back to pages"
            className="rounded-full border border-white/15 p-2.5 transition-colors hover:border-pumpkin hover:text-pumpkin"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="min-w-0">
            <div className="truncate font-display text-xl leading-none text-pumpkin sm:text-2xl">
              {page.title}
            </div>
            <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-paper/50">
              {cat?.name}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            data-testid="studio-progress-counter"
            className="hidden rounded-full border border-white/15 px-3 py-1.5 text-xs font-medium text-paper/70 sm:block"
          >
            {colored}/{fillable} spots colored
          </span>
          <button
            data-testid="studio-mute-btn"
            onClick={toggleMute}
            aria-label={muted ? "Unmute" : "Mute"}
            className="rounded-full border border-white/15 p-2.5 transition-colors hover:border-slime hover:text-slime"
          >
            {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
          <button
            data-testid="studio-finish-btn"
            disabled={!ready || busy}
            onClick={onFinish}
            className={`flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-bold transition-all sm:px-6 ${
              ready
                ? "bg-slime text-ink shadow-glow-slime animate-pulseglow hover:scale-105"
                : "cursor-not-allowed bg-white/10 text-paper/40"
            }`}
          >
            <PartyPopper size={16} /> Finish
          </button>
        </div>
      </header>

      {locked ? (
        <div
          data-testid="studio-locked-screen"
          className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center"
        >
          <span className="animate-floaty rounded-full bg-pumpkin p-5 text-ink shadow-glow-orange">
            <Lock size={34} />
          </span>
          <h2 className="font-display text-4xl text-paper">This page is still sleeping...</h2>
          <p className="max-w-sm text-paper/60">
            Finish more pages to crack this secret category open!
          </p>
          <button
            data-testid="studio-locked-back-btn"
            onClick={() => navigate("/")}
            className="rounded-full bg-pumpkin px-7 py-3 font-bold text-ink"
          >
            Back to the Vault
          </button>
        </div>
      ) : (
        <div className="flex flex-1 flex-col-reverse md:flex-row">
          <main className="relative flex flex-1 items-center justify-center p-3 sm:p-6">
            <div
              className="relative w-full max-w-[560px] rounded-3xl bg-[#FFFDF8] p-2 shadow-[0_0_60px_rgba(255,122,0,0.15)]"
              data-testid="studio-canvas-wrap"
            >
              <PageSVG
                page={page}
                fills={state.fills}
                strokes={state.strokes}
                placed={state.placed}
                interactive
                svgRef={svgRef}
                data-testid="studio-canvas-svg"
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                className="block w-full touch-none select-none"
              >
                {live && (
                  <path
                    d={pointsToPath(live.points)}
                    stroke={live.color}
                    strokeWidth={live.size}
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    pointerEvents="none"
                  />
                )}
              </PageSVG>
              <AnimatePresence>
                {sparks.map((sp) => (
                  <motion.span
                    key={sp.id}
                    className="pointer-events-none absolute z-10 h-3 w-3 rounded-full"
                    style={{ left: sp.x - 6, top: sp.y - 6, background: sp.c, boxShadow: `0 0 14px ${sp.c}` }}
                    initial={{ scale: 0.4, opacity: 1 }}
                    animate={{ scale: 3.2, opacity: 0, y: -26 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.65, ease: "easeOut" }}
                  />
                ))}
              </AnimatePresence>
              {ready && !result && (
                <div className="pointer-events-none absolute -top-3 right-4 rounded-full bg-slime px-3 py-1 text-[11px] font-bold text-ink shadow-glow-slime">
                  Ready to claim reward!
                </div>
              )}
            </div>
          </main>

          <aside className="shrink-0 border-t border-white/10 bg-night/60 p-3 md:w-64 md:border-l md:border-t-0">
            <div className="flex items-center gap-2 md:flex-col md:items-stretch md:gap-4">
              <div className="flex gap-2 md:justify-center">
                {toolBtn("fill", <PaintBucket size={22} />, "Fill tool")}
                {toolBtn("brush", <Brush size={22} />, "Brush tool")}
                {toolBtn("eraser", <Eraser size={22} />, "Eraser tool")}
                {toolBtn("sticker", <StickerIcon size={22} />, "Sticker tool")}
              </div>

              <div className="min-w-0 flex-1 md:flex-none">
                <div
                  className="flex gap-1.5 overflow-x-auto rounded-2xl border border-white/10 bg-white/5 p-2 no-scrollbar md:grid md:grid-cols-8 md:overflow-visible"
                  data-testid="studio-palette"
                >
                  {PALETTE.map((c, i) => (
                    <button
                      key={c}
                      data-testid={`palette-color-${i}`}
                      onClick={() => {
                        setColor(c);
                        if (tool === "eraser" || tool === "sticker") setTool("fill");
                        sfx.click();
                      }}
                      aria-label={`Color ${c}`}
                      className={`h-8 w-8 shrink-0 rounded-full border-2 transition-transform hover:scale-110 ${
                        color === c ? "scale-110 border-slime" : "border-white/20"
                      }`}
                      style={{ background: c }}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-3 flex items-center gap-3 md:mt-4 md:flex-col md:items-stretch md:gap-3">
              {tool === "brush" ? (
                <label className="flex flex-1 items-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-3 py-2 md:flex-none">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-paper/60">Size</span>
                  <input
                    data-testid="brush-size-slider"
                    type="range"
                    min="2"
                    max="26"
                    value={brushSize}
                    onChange={(e) => setBrushSize(+e.target.value)}
                    className="w-full accent-[#FF7A00]"
                  />
                </label>
              ) : tool === "sticker" ? (
                <>
                  <div className="flex flex-1 gap-1.5 overflow-x-auto rounded-2xl border border-white/10 bg-white/5 p-2 no-scrollbar md:flex-none" data-testid="sticker-picker">
                    {STICKERS.map((st) => {
                      const owned = STICKERS_BY_ID[st.id];
                      return (
                        <button
                          key={st.id}
                          data-testid={`sticker-pick-${st.id}`}
                          onClick={() => {
                            setStickerId(st.id);
                            sfx.boop();
                          }}
                          aria-label={`Sticker ${st.name}`}
                          className={`h-9 w-9 shrink-0 rounded-xl border-2 p-0.5 ${
                            stickerId === st.id ? "border-slime" : "border-transparent opacity-70"
                          }`}
                        >
                          <svg viewBox="0 0 48 48" className="h-full w-full">{owned.art}</svg>
                        </button>
                      );
                    })}
                  </div>
                  <label className="flex items-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-3 py-2">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-paper/60">Big</span>
                    <input
                      data-testid="sticker-size-slider"
                      type="range"
                      min="0.6"
                      max="1.8"
                      step="0.1"
                      value={stickerSize}
                      onChange={(e) => setStickerSize(+e.target.value)}
                      className="w-24 accent-[#39FF14]"
                    />
                  </label>
                </>
              ) : (
                <p className="hidden md:block px-1 text-xs leading-relaxed text-paper/50">
                  Tap any spot to flood it with color. Switch to the brush to
                  scribble freely, or drop stickers from your reward jar!
                </p>
              )}
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2 md:mt-4 md:justify-center">
              <button
                data-testid="studio-undo-btn"
                disabled={!state.past.length}
                onClick={() => {
                  dispatch({ type: "UNDO" });
                  sfx.click();
                }}
                aria-label="Undo"
                className="flex h-11 w-11 items-center justify-center rounded-2xl border border-white/15 bg-white/5 transition-colors hover:border-pumpkin hover:text-pumpkin disabled:opacity-30"
              >
                <Undo2 size={18} />
              </button>
              <button
                data-testid="studio-redo-btn"
                disabled={!state.future.length}
                onClick={() => {
                  dispatch({ type: "REDO" });
                  sfx.click();
                }}
                aria-label="Redo"
                className="flex h-11 w-11 items-center justify-center rounded-2xl border border-white/15 bg-white/5 transition-colors hover:border-pumpkin hover:text-pumpkin disabled:opacity-30"
              >
                <Redo2 size={18} />
              </button>
              <button
                data-testid="studio-clear-btn"
                onClick={clearAll}
                className={`flex h-11 items-center gap-2 rounded-2xl border px-4 text-sm font-semibold transition-all ${
                  clearArmed
                    ? "border-candy bg-candy/15 text-candy"
                    : "border-white/15 bg-white/5 text-paper/80 hover:border-candy hover:text-candy"
                }`}
              >
                <Trash2 size={16} /> {clearArmed ? "Sure?" : "Clear"}
              </button>
              <button
                data-testid="studio-download-btn"
                onClick={downloadPng}
                className="flex h-11 items-center gap-2 rounded-2xl border border-white/15 bg-white/5 px-4 text-sm font-semibold transition-colors hover:border-slime hover:text-slime"
              >
                <Download size={16} /> Download
              </button>
              <button
                data-testid="studio-save-btn"
                onClick={saveToGallery}
                disabled={saved}
                className="flex h-11 items-center gap-2 rounded-2xl bg-pumpkin px-4 text-sm font-bold text-ink shadow-glow-orange transition-transform hover:scale-105 disabled:opacity-60"
              >
                <Save size={16} /> {saved ? "Saved!" : "Save"}
              </button>
            </div>
          </aside>
        </div>
      )}

      <AnimatePresence>
        {result && (
          <motion.div
            data-testid="celebration-modal"
            className="fixed inset-0 z-[90] flex items-center justify-center bg-ink/85 backdrop-blur-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Confetti />
            <motion.div
              initial={{ scale: 0.6, y: 60, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              transition={{ type: "spring", stiffness: 180, damping: 16 }}
              className="relative mx-4 w-full max-w-md rounded-3xl border-2 border-pumpkin/50 bg-night p-8 text-center shadow-frame"
            >
              <div className="font-display text-4xl text-pumpkin text-glow-orange sm:text-5xl">
                Scream-tastic!
              </div>
              <p className="mt-2 text-paper/70">
                You colored {colored} spots on {page.title}!
              </p>

              {result.new_sticker && (
                <motion.div
                  initial={{ scale: 0, rotate: -30 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ delay: 0.4, type: "spring", stiffness: 200, damping: 10 }}
                  className="mx-auto mt-6 flex flex-col items-center gap-2 rounded-2xl border border-slime/40 bg-slime/10 px-8 py-5"
                  data-testid="celebration-sticker"
                >
                  <svg viewBox="0 0 48 48" className="h-20 w-20">
                    {STICKERS_BY_ID[result.new_sticker].art}
                  </svg>
                  <span className="text-sm font-bold text-slime">
                    New sticker: {STICKERS_BY_ID[result.new_sticker].name}!
                  </span>
                </motion.div>
              )}

              {result.new_unlock && (
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.7 }}
                  className="mt-4 rounded-2xl border-2 border-pumpkin bg-pumpkin/15 px-4 py-3"
                  data-testid="celebration-unlock"
                >
                  <div className="flex items-center justify-center gap-2 font-display text-2xl text-pumpkin">
                    <Lock size={20} /> SECRET UNLOCKED!
                  </div>
                  <div className="mt-1 text-sm font-semibold text-paper">
                    {result.new_unlock.name} pages are now open in the Vault
                  </div>
                </motion.div>
              )}

              <div className="mt-7 grid gap-2.5">
                <button
                  data-testid="celebration-save-btn"
                  onClick={saveToGallery}
                  disabled={saved}
                  className="rounded-full bg-pumpkin py-3 font-bold text-ink shadow-glow-orange transition-transform hover:scale-[1.03] disabled:opacity-60"
                >
                  {saved ? "Hung in the Gallery!" : "Save to Haunted Gallery"}
                </button>
                <button
                  data-testid="celebration-download-btn"
                  onClick={downloadPng}
                  className="rounded-full border-2 border-slime/60 py-3 font-semibold text-slime transition-colors hover:bg-slime/10"
                >
                  Download my masterpiece
                </button>
                <div className="flex gap-2.5">
                  <button
                    data-testid="celebration-keep-btn"
                    onClick={() => setResult(null)}
                    className="flex-1 rounded-full border border-white/20 py-3 text-sm font-semibold text-paper/80 transition-colors hover:border-paper/50"
                  >
                    Keep decorating
                  </button>
                  <button
                    data-testid="celebration-done-btn"
                    onClick={() => navigate("/")}
                    className="flex-1 rounded-full border border-white/20 py-3 text-sm font-semibold text-paper/80 transition-colors hover:border-paper/50"
                  >
                    Color another
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {!loaded && <div className="fixed inset-0 z-[95] bg-ink" />}
    </div>
  );
}

export default function Studio() {
  const { pageId } = useParams();
  const builtIn = pageId && !pageId.startsWith("custom-") ? getPage(pageId) : null;
  if (builtIn && builtIn.imagePage) {
    return (
      <ImageStudio
        routePageId={pageId}
        staticImage={builtIn.image}
        staticTitle={builtIn.title}
        staticPageId={builtIn.id}
      />
    );
  }
  if (pageId && pageId.startsWith("custom-")) {
    return <ImageStudio routePageId={pageId} />;
  }
  return <SvgStudio />;
}
